import axios from 'axios';
import oauth from 'axios-oauth-client';
import tokenProvider from 'axios-token-interceptor';
import pRetry from 'p-retry';
import { FORM_ERROR } from 'final-form';
import { application } from '../constants/configs';
import i18n from './i18n';
import { store } from '../AppRenderer';
import { logoutUser } from '../redux/authUser/actions';
import { NotificationManager } from '../components/common/react-notifications';

function getMaxAge(res) {
  return res.expires_in * 1000;
}

function headerFormatter(res) {
  return 'Bearer ' + res.access_token;
}

class Api {
  _cachedToken;

  /**
   * Oauth password grant
   * @param {string} username
   * @param {string} password
   * @return {Promise<object>}
   */
  passwordGrant = (username, password) => {
    return pRetry(
      async () => {
        try {
          const getOwnerCredentials = await oauth.client(
            axios.create({
              timeout: 10000,
            }),
            {
              url:
                application.adminCoreEndpoint + application.tokenPath,
              grant_type: 'password',
              client_id: application.id,
              username,
              password,
            }
          );
          const response = await getOwnerCredentials();

          localStorage.setItem(
            'refreshToken',
            response.refresh_token
          );

          this._cachedToken = tokenProvider.tokenCache(
            () => Promise.resolve(response),
            {
              getMaxAge,
            }
          );

          return response;
        } catch (e) {
          if (
            e.response &&
            Math.floor(e.response.status / 100) === 4
          ) {
            throw new pRetry.AbortError(e);
          } else if (!e.request) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      }
    );
  };

  /**
   * Oauth refresh token
   * @return {Promise<object>}
   */
  refreshToken = () => {
    return pRetry(
      async () => {
        if (!localStorage.getItem('refreshToken')) {
          const e = new Error('Refresh token not found');
          e.suppressEmitNotification = true;
          throw new pRetry.AbortError(e);
        }

        try {
          const getRefreshToken = oauth.client(
            axios.create({
              timeout: 5000,
            }),
            {
              url:
                application.adminCoreEndpoint + application.tokenPath,
              grant_type: 'refresh_token',
              client_id: application.id,
              refresh_token: localStorage.getItem('refreshToken'),
            }
          );

          const response = await getRefreshToken();

          if (response.refresh_token) {
            localStorage.setItem(
              'refreshToken',
              response.refresh_token
            );
          }

          return response;
        } catch (e) {
          if (
            (e.response &&
              Math.floor(e.response.status / 100) === 4) ||
            !e.request
          ) {
            this.clearCachedToken();
            store.dispatch(logoutUser());
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      }
    );
  };

  clearCachedToken = () => {
    this._cachedToken = undefined;
  };

  /**
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams|FormData|File|Blob} data
   * @param {object} options
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   * @private
   */
  _invoke = async (method, path, data, options) => {
    const currentLanguage = i18n.language;

    const instance = axios.create({
      method,
      url: path,
      headers: {
        'Accept-Language': `${currentLanguage}, en;q=0.8, *;q=0.5`,
        'Cache-Control': 'no-cache',
      },
      data,
      timeout: 5000,
      ...options,
    });

    if (this._cachedToken) {
      instance.interceptors.request.use(
        tokenProvider({
          getToken: this._cachedToken,
          headerFormatter,
        })
      );
    } else {
      const hasRefreshToken = !!localStorage.getItem('refreshToken');
      if (hasRefreshToken) {
        if (!this._cachedToken) {
          this._cachedToken = tokenProvider.tokenCache(
            this.refreshToken,
            {
              getMaxAge,
            }
          );
        }
        instance.interceptors.request.use(
          tokenProvider({
            getToken: this._cachedToken,
            headerFormatter,
          })
        );
      }
    }

    try {
      return await instance.request();
    } catch (e) {
      if (e.response && e.response.status === 401) {
        this._cachedToken = tokenProvider.tokenCache(
          this.refreshToken,
          {
            getMaxAge,
          }
        );
        return this._invoke(method, path, data, options);
      } else if (!e.request) {
        throw new pRetry.AbortError(e);
      }
      throw e;
    }
  };

  /**
   * Invoke API method
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams} [data]
   * @param {object} [options]
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   */
  invoke = (method, path, data, options) => {
    const apiOptions = {
      emitNotification: true,
      ...((options && options.api) || {}),
    };

    if (options) {
      delete options.api;
    }
    return pRetry(
      async () => {
        try {
          return await this._invoke(method, path, data, options);
        } catch (e) {
          if (
            e.response &&
            Math.floor(e.response.status / 100) === 4
          ) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: method.toUpperCase() === 'GET' ? 3 : 0,
        factor: 2.4477,
      }
    ).catch((e) => {
      if (
        apiOptions.emitNotification &&
        !e.suppressEmitNotification
      ) {
        let message;

        if (
          e.response &&
          e.response.data &&
          e.response.data.message
        ) {
          message = e.response.data.message;
        } else if (
          e.response &&
          e.response.data &&
          e.response.data.error === 'invalid_grant'
        ) {
          message = null;
        } else if (e.request) {
          message = i18n.t(
            'Check your internet connection and try again'
          );
        } else {
          message = i18n.t('An error occurred');
        }

        if (message) {
          NotificationManager.error(
            message,
            i18n.t('Error!'),
            undefined,
            undefined,
            undefined,
            'filled'
          );
        }
      }

      if (e.response && e.response.data) {
        if (e.response.data.details) {
          e.filedError = e.response.data.details.reduce(
            (obj, item) => {
              const field = item['field'].split('/')[0];
              if (!obj.hasOwnProperty(field)) {
                obj[field] = item['issue'];
              }
              return obj;
            },
            {}
          );
        } else if (e.response.data.message) {
          e.filedError = {
            [FORM_ERROR]: e.response.data.message,
          };
        }
      } else if (e.request) {
        e.filedError = {
          [FORM_ERROR]: i18n.t(
            'Check your internet connection and try again'
          ),
        };
      }

      if (!e.filedError) {
        e.filedError = {
          [FORM_ERROR]: i18n.t('An error occurred'),
        };
      }

      throw e;
    });
  };
}

export default new Api();
